"use client";

// Data layer Promo / Cuci Gudang (mig 3470). Helper murni & tipe ada di
// src/lib/promo.ts. Halaman admin (/admin/promo) otomatis memakai sesi admin
// via scope.ts; storefront memakai scope "store" eksplisit.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { getQueryClient } from "@/lib/queryClient";
import { terjemahkanErrorRpc } from "@/lib/rpcError";
import { mapPromoAuditRows, type PromoAuditEntry } from "@/lib/promoAudit";
import type { ProductVariation } from "@/lib/products";
import {
  mapPromoRow,
  promoBoundaryTimerMs,
  promoNextBoundaryMs,
  promosForProduct,
  promosForStore,
  type ProductPromo,
  type PromoCampaign,
  type PromoProductRef
} from "@/lib/promo";

// Embed `promo_campaign_rules` (mig 3670) ikut di SEMUA pembaca kampanye —
// termasuk jalur ANON storefront, karena aturan ukuran ikut menentukan HARGA.
// Kalau satu pembaca melewatkannya, layar itu diam-diam memakai potongan utama
// sementara layar lain memakai aturan ukuran: dua harga untuk barang yang sama.
export const PROMO_SELECT =
  "id, store_id, name, description, is_active, starts_at, ends_at, discount_mode, discount_value, " +
  "product_type_scope, target_type, size_rules_exclusive, level_slots, level_policy, " +
  // mig 6010: saklar kasir & never_worse + peran target (include/exclude).
  "apply_to_pos, never_worse, " +
  // mig 6020: kuota kampanye & batas per pembeli (NULL = tak terbatas).
  "quota_total, per_customer_limit, " +
  "promo_campaign_targets(kind, product_id, name, mode), " +
  "promo_campaign_rules(axis, match_value, discount_mode, discount_value)";

/** Semua kampanye (admin melihat aktif & nonaktif), terbaru dulu. */
export async function fetchPromoCampaignsAdmin(): Promise<PromoCampaign[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const { data, error } = await db
    .from("promo_campaigns")
    .select(PROMO_SELECT)
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapPromoRow);
}

/**
 * Kampanye AKTIF saja — dipakai storefront DAN layar produk admin.
 * Periode TIDAK disaring di sini (biarkan `isPromoRunning` di klien
 * mengerjakannya) supaya satu hasil cache bisa dipakai lintas menit tanpa
 * membuat kampanye yang baru mulai/berakhir butuh refetch.
 *
 * MULTI-TENANT — FAIL-CLOSED, sengaja beda dari popups/branding.
 * Kebijakan RLS store-scoped memakai cabang ESCAPE (`current_store_id() is null
 * or ...`, mig 3040) supaya storefront publik hidup; artinya sesi ANON lolos
 * dan melihat baris SEMUA toko. Untuk popup/branding, gagal-resolusi cukup
 * "fail-open" karena paling buruk tampil salah merek. Di sini taruhannya HARGA,
 * dan target kategori/merek dicocokkan BY NAMA — kampanye "Gamis −50%" milik
 * toko A akan mendiskon "Gamis" milik toko B, alias menjual barang orang lain
 * setengah harga. Maka bila toko tak ter-resolusi kita memulangkan KOSONG:
 * paling buruk pembeli melihat harga normal, tak pernah harga toko lain.
 */
export async function fetchActivePromoCampaigns(): Promise<PromoCampaign[]> {
  const db = createBrowserSupabase("store");
  if (!db) return [];

  const storeId = getClientStoreId();
  if (!storeId) return [];

  const { data, error } = await db
    .from("promo_campaigns")
    .select(PROMO_SELECT)
    .eq("is_active", true)
    .eq("store_id", storeId);

  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapPromoRow);
}

/**
 * Sisa kuota (mig 6020) untuk kampanye yang MEMANG berkuota — `campaignId` →
 * pcs tersisa. Dipanggil dari `PromoContext` untuk chip "Sisa N pcs" dan untuk
 * menggugurkan kampanye yang habis sebelum harganya sempat ditawarkan.
 *
 * Satu RPC per kampanye, SENGAJA: kampanye berkuota jumlahnya hitungan jari
 * (pemanggilnya sudah menyaring `quotaTotal !== null`), dan `promo_campaign_
 * remaining` menghitung Σ qty pesanan hidup — memaksanya jadi satu panggilan
 * masal berarti fungsi SQL baru yang harus ikut dijaga gerbang & lint. Daftar
 * kosong = nol panggilan.
 *
 * Kampanye yang GAGAL dibaca sengaja TIDAK dimasukkan ke peta: kunci yang absen
 * berarti "tak diketahui" (kampanyenya tetap ditawarkan), sedangkan memasukkan
 * 0 akan mematikan promo yang sebenarnya masih punya sisa.
 */
export async function fetchPromoRemaining(
  campaignIds: readonly string[]
): Promise<Map<string, number | null>> {
  const out = new Map<string, number | null>();
  if (!campaignIds.length) return out;
  const db = createBrowserSupabase("store");
  if (!db) return out;

  await Promise.all(
    campaignIds.map(async (id) => {
      const { data, error } = await db.rpc("promo_campaign_remaining", { p_campaign: id });
      if (error) return;
      if (data === null || data === undefined) {
        // NULL = kampanye tanpa kuota (tak terbatas) — dicatat apa adanya.
        out.set(id, null);
        return;
      }
      const n = Number(data);
      if (Number.isFinite(n)) out.set(id, Math.max(0, Math.trunc(n)));
    })
  );
  return out;
}

/**
 * Versi sesi ADMIN dari daftar kampanye aktif — dipakai badge "Promo" di Daftar
 * Produk, banner peringatan di tab Harga ProductForm, dan (lewat
 * `fetchPromosForProduct("admin")` / `fetchLevelDiscountDefaults`) harga baris
 * yang admin input. Sesi admin ber-JWT store claim sudah di-scope RLS; tetap
 * dipakai `is_active` agar kampanye nonaktif tak ikut menandai produk.
 */
export async function fetchActivePromoCampaignsAdmin(): Promise<PromoCampaign[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];

  const { data, error } = await db
    .from("promo_campaigns")
    .select(PROMO_SELECT)
    .eq("is_active", true);

  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const campaigns = ((data as any[]) ?? []).map(mapPromoRow);
  // Audit A6: RLS admin memakai cabang escape `current_store_id() is null or …`
  // — pemilik platform tanpa toko di profil membaca kampanye SEMUA toko, dan
  // pencocokan target BY NAMA lalu mem-prefill promo toko lain ke nota /
  // keranjang admin. Saring ke toko host ini (di-set <Providers> dari resolusi
  // Host).
  //
  // Host TAK ter-resolusi (apex platform, localhost, resolusi Host lewat batas
  // waktu) → daftar APA ADANYA, bukan kosong. Ini sesi ADMIN yang sudah
  // di-scope RLS ke toko admin ber-JWT — bukan jalur anon yang diikat aturan
  // A2 — dan "kosong" di sini punya harga: keranjang admin & prefill nota
  // menghitung TANPA promo, sehingga pesanan yang admin input jadi lebih
  // mahal dari yang pembeli lihat di storefront (audit pasca-rilis
  // 2026-09-17). Jalur uang tetap menyaring ke `products.store_id` sendiri
  // (`fetchPromosForProduct`, `fetchLevelDiscountDefaults`); yang tersisa
  // tanpa saringan host hanyalah penanda "sedang promo" di layar admin.
  const hostStore = getClientStoreId();
  return hostStore ? promosForStore(campaigns, hostStore) : campaigns;
}


/**
 * Kampanye AKTIF + jam dinding untuk layar ADMIN — bahan penanda B ("produk
 * sedang promo", lihat `promoLineState`).
 *
 * SENGAJA tidak memakai `PromoContext`: context itu memakai sesi "store" (anon)
 * + `getClientStoreId()`, dan di host lokal `resolve_store_public` melempar ke
 * fallback toko legacy — persis alasan `fetchActivePromoCampaignsAdmin` ada.
 * Di layar admin, sesi admin-lah yang benar; RLS sudah men-scope-nya per toko.
 *
 * `now` mulai `null` lalu diisi setelah mount (idiom AdminPromo): tanpa itu
 * jam server & jam klien bisa berdiri di seberang `ends_at`.
 */
export function useActivePromosAdmin(opts?: { live?: boolean }): {
  campaigns: PromoCampaign[];
  now: Date | null;
} {
  const live = opts?.live === true;
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(new Date());
    if (!live) return;
    // Jam dinding BERDETAK (bughunt D16-18) — `live`, HANYA layar yang
    // MENGUNCI harga dengan jam ini (Kasir: terbuka seharian, `pos_checkout`
    // menerima harga baris apa adanya). Jam yang beku sejak mount terus
    // mendiskon kampanye yang sudah berakhir. Cermin detak 60 dtk
    // `PromoContext`; pemakai lencana per baris (keranjang admin, editor nota)
    // sengaja tak berdetak — ratusan baris = ratusan timer.
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [live]);

  const { data } = useQuery({
    queryKey: ["promo", "active", "admin"],
    queryFn: fetchActivePromoCampaignsAdmin,
    staleTime: 120_000
  });

  // Diputar TEPAT di batas kampanye terdekat juga; tundaannya diklem
  // (`promoBoundaryTimerMs`, kelas bug loop render zzhomey 2026-09-17).
  const campaigns = data ?? [];
  const nextBoundary = live ? promoNextBoundaryMs(campaigns, now) : null;
  useEffect(() => {
    const delay = promoBoundaryTimerMs(nextBoundary);
    if (delay === null) return;
    const id = window.setTimeout(() => setNow(new Date()), delay);
    return () => window.clearTimeout(id);
  }, [nextBoundary]);

  return { campaigns, now };
}

/**
 * Nama kampanye yang SEDANG berjalan untuk sebuah produk (penanda B). Kosong
 * bila jamnya belum ada — jangan menebak, lihat catatan hidrasi di atas.
 */
export function runningPromoNamesFor(
  campaigns: readonly PromoCampaign[],
  product: PromoProductRef,
  now: Date | null
): string[] {
  if (!now || !campaigns.length) return [];
  return promosForProduct(campaigns, product, now).map((p) => p.name);
}

/**
 * Baris produk seukuran yang dibutuhkan pencocokan target kampanye (merek /
 * kategori / id / jenis) + toko pemiliknya. MELEMPAR bila query gagal — dulu
 * galat baris produk ditelan jadi `data: null` → "tak ada promo", tanpa tanda.
 */
async function fetchPromoProductRef(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  productId: string
): Promise<{ ref: PromoProductRef; storeId: string | null } | null> {
  const { data, error } = await db
    .from("products")
    .select("id, brand, category, product_type, store_id")
    .eq("id", productId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const row = data as {
    id: string;
    brand: string | null;
    category: string | null;
    product_type: string | null;
    store_id: string | null;
  };
  return {
    storeId: row.store_id,
    ref: {
      id: row.id,
      brand: row.brand,
      category: row.category,
      productType: row.product_type === "ready_stock" ? "ready_stock" : "preorder"
    }
  };
}

/**
 * Inti `fetchPromosForProduct` yang MELEMPAR saat gagal — untuk jalur yang
 * harus membedakan "tak ada promo" dari "tak tahu" (checkout, audit A5).
 * Pemanggil lain memakai pembungkus best-effort di bawah.
 */
export async function fetchPromosForProductStrict(
  productId: string,
  scope: "store" | "admin" = "store"
): Promise<ProductPromo[]> {
  if (!productId) return [];
  const db = createBrowserSupabase(scope);
  if (!db) return [];

  const [campaigns, product] = await Promise.all([
    scope === "admin" ? fetchActivePromoCampaignsAdmin() : fetchActivePromoCampaigns(),
    fetchPromoProductRef(db, productId)
  ]);
  if (!campaigns.length || !product) return [];

  // Audit A6: kampanye HARUS setoko dengan produknya — target merek/kategori
  // dicocokkan BY NAMA, jadi "Gamis" toko lain akan mendiskon "Gamis" toko ini.
  return promosForProduct(promosForStore(campaigns, product.storeId), product.ref, new Date());
}

/**
 * Promo yang berlaku untuk SATU produk — satu pintu yang dipakai jalur checkout
 * pembeli MAUPUN pembuat/pengedit pesanan sisi admin.
 *
 * Kenapa perlu memuat baris produknya: target kampanye dicocokkan ke merek /
 * kategori / id produk, dan pemanggil biasanya hanya memegang productId. Satu
 * SELECT ringan, bukan produk lengkap.
 *
 * `scope` menentukan sesi mana yang dipakai — storefront memakai sesi "store"
 * (anon, ter-scope toko & fail-closed), layar admin memakai sesi "admin".
 * Keduanya WAJIB menghasilkan harga yang sama; kalau tidak, pesanan yang
 * di-input admin akan berbeda harga dari pesanan yang dibuat pembeli sendiri.
 *
 * Best-effort: kegagalan apa pun → [] (harga tanpa promo), tak pernah melempar.
 * Jalur yang perlu tahu bahwa ia GAGAL (bukan "tak ada promo") memakai
 * `fetchPromosForProductStrict`.
 */
export async function fetchPromosForProduct(
  productId: string,
  scope: "store" | "admin" = "store"
): Promise<ProductPromo[]> {
  try {
    return await fetchPromosForProductStrict(productId, scope);
  } catch {
    return [];
  }
}

/**
 * CADANGAN checkout (audit A5, 2026-09-17): kampanye yang SUDAH DIMUAT
 * storefront — cache TanStack `PromoContext` (kunci `["promo","active",storeId]`,
 * di-seed root layout, dimuat ulang tepat di batas kampanye) — dicocokkan ke
 * baris produk dari DB. Dipakai hanya setelah fetcher strict gagal dua kali.
 *
 * `null` = TAK TAHU (tanpa cache / baris produk pun gagal) — pemanggil jatuh
 * ke harga tanpa promo dan mencatat kode peringatan; `[]` = cache tahu tak ada
 * kampanye. Tak pernah melempar, dan TAK PERNAH memakai angka keranjang:
 * localStorage bisa diedit siapa pun, sementara insert `order_items` tak punya
 * gerbang harga server (mig 5420 hanya `unit_price >= 0`).
 */
export async function promosForProductFromClientCache(
  productId: string
): Promise<ProductPromo[] | null> {
  try {
    const storeId = getClientStoreId();
    if (!storeId) return null;
    const cached = getQueryClient().getQueryData<PromoCampaign[]>(["promo", "active", storeId]);
    if (!Array.isArray(cached)) return null;
    if (!cached.length) return [];
    const db = createBrowserSupabase("store");
    if (!db) return null;
    const product = await fetchPromoProductRef(db, productId);
    if (!product) return null;
    return promosForProduct(promosForStore(cached, product.storeId), product.ref, new Date());
  } catch {
    return null;
  }
}

/** Satu kampanye by id (editor admin). null bila tidak ditemukan. */
export async function fetchPromoCampaignById(id: string): Promise<PromoCampaign | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("promo_campaigns")
    .select(PROMO_SELECT)
    .eq("id", id)
    .maybeSingle();

  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data ? mapPromoRow(data as any) : null;
}

/** Simpan kampanye + ganti seluruh targetnya dalam SATU transaksi (RPC 3470). */
export async function savePromoCampaign(payload: {
  campaign: Record<string, unknown>;
  targets: Array<Record<string, unknown>>;
}): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");

  const { data, error } = await db.rpc("admin_upsert_promo_campaign", {
    p_campaign: payload.campaign,
    p_targets: payload.targets
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/** Nyalakan/matikan kampanye tanpa membuka editornya. */
export async function setPromoCampaignActive(id: string, isActive: boolean): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");

  // Audit A8 (mig 5990): lewat RPC ber-audit, bukan UPDATE langsung — aturan
  // repo "aksi = RPC", dan jejak siapa-mengubah-apa hidup di
  // `promo_campaign_audit_log`.
  const { error } = await db.rpc("admin_set_promo_campaign_active", {
    p_id: id,
    p_active: isActive
  });
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
}

/** Riwayat perubahan satu kampanye (siapa, kapan, kolom apa dari→ke), terbaru dulu. */
export async function fetchPromoAuditLogAdmin(campaignId: string): Promise<PromoAuditEntry[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];
  const { data, error } = await db
    .from("promo_campaign_audit_log")
    .select("id, campaign_id, campaign_name, actor_id, actor_name, action, changes, created_at")
    .eq("campaign_id", campaignId)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return mapPromoAuditRows(data);
}

/** Satu produk contoh untuk kartu pratinjau dampak di editor kampanye. */
export type PromoPreviewProduct = {
  id: string;
  name: string;
  brand: string | null;
  category: string | null;
  productType: "preorder" | "ready_stock";
  /** Harga & modal varian PERTAMA yang ber-harga — cermin aturan kartu katalog. */
  priceNormal: number | null;
  hpp: number | null;
  /**
   * Seluruh varian aktif ber-harga (R3): `pricing` = bentuk `explainVariantPrice`
   * (override diskon per-slot ikut), `hpp` = modal varian itu. Pratinjau
   * menghitung rentang harga termurah–termahal per level dari sini, dengan
   * tangga produksi — bukan rumus tandingan.
   */
  variants: Array<{ pricing: ProductVariation; hpp: number | null }>;
};

/**
 * Beberapa produk yang akan terkena kampanye — bahan kartu "Pratinjau Dampak".
 * Sengaja dibatasi (default 6): ini alat bantu keputusan, bukan laporan.
 */
export async function fetchPromoPreviewProducts(opts: {
  targetType: "product" | "category" | "brand" | "all";
  targets: Array<{ kind: string; productId: string | null; name: string }>;
  productTypeScope: "preorder" | "ready_stock" | "all";
  limit?: number;
}): Promise<PromoPreviewProduct[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  let query = db
    .from("products")
    .select(
      // R3: override diskon per-slot varian ikut dimuat — pratinjau harus
      // menghitung dengan tangga yang sama dengan storefront.
      "id, name, brand, category, product_type, " + PROMO_PRODUCT_VARIANT_COLS
    )
    .eq("is_active", true)
    .limit(opts.limit ?? 6);

  if (opts.productTypeScope !== "all") query = query.eq("product_type", opts.productTypeScope);

  if (opts.targetType === "product") {
    const ids = opts.targets.map((t) => t.productId).filter((id): id is string => Boolean(id));
    if (!ids.length) return [];
    query = query.in("id", ids);
  } else if (opts.targetType === "brand" || opts.targetType === "category") {
    const names = opts.targets.map((t) => t.name).filter(Boolean);
    if (!names.length) return [];
    query = query.in(opts.targetType, names);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapPromoProductRow);
}

/**
 * Baris `products` (+ embed varian) → bentuk yang dipakai pratinjau editor DAN
 * layar pemilih produk. SATU mapper karena keduanya menghitung harga dengan
 * tangga produksi yang sama (`explainVariantPrice`); dua salinan berarti dua
 * layar bisa memajang harga berbeda untuk produk yang sama.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPromoProductRow(row: any): PromoPreviewProduct {
  {
    type Raw = {
      id: string;
      name: string | null;
      price_normal: number | string | null;
      price_extra?: number | string | null;
      hpp: number | string | null;
      sort_order: number | null;
      is_active: boolean | null;
      diskon_mode: string | null;
      diskon_reseller: number | string | null;
      diskon_agen: number | string | null;
      diskon_slot3: number | string | null;
      diskon_slot4: number | string | null;
      diskon_slot5: number | string | null;
      diskon_slot6: number | string | null;
      diskon_slot7: number | string | null;
      diskon_slot8: number | string | null;
      diskon_slot9: number | string | null;
      diskon_slot10: number | string | null;
      diskon_slot11: number | string | null;
      diskon_slot12: number | string | null;
      diskon_slot13: number | string | null;
      diskon_slot14: number | string | null;
      diskon_slot15: number | string | null;
    };
    const variants = (row.product_variants ?? []) as Raw[];
    const sorted = [...variants]
      .filter((v) => v.is_active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const first = sorted.find((v) => v.price_normal !== null && v.price_normal !== undefined);
    const toNum = (raw: number | string | null | undefined) =>
      raw === null || raw === undefined ? null : Number(raw);
    const opt = (raw: number | string | null | undefined): number | undefined => {
      const n = toNum(raw);
      return n === null || !Number.isFinite(n) ? undefined : n;
    };
    return {
      id: row.id as string,
      name: (row.name as string) ?? "",
      brand: (row.brand as string | null) ?? null,
      category: (row.category as string | null) ?? null,
      productType: row.product_type === "ready_stock" ? "ready_stock" : "preorder",
      priceNormal: toNum(first?.price_normal),
      hpp: toNum(first?.hpp),
      variants: sorted
        .filter((v) => v.price_normal !== null && v.price_normal !== undefined)
        .map((v) => ({
          pricing: {
            id: v.id,
            name: v.name ?? "",
            sku: "",
            price_normal: Number(v.price_normal),
            price_extra: opt(v.price_extra),
            diskonMode:
              v.diskon_mode === "nominal" ? "nominal" : v.diskon_mode === "persen" ? "persen" : undefined,
            diskonReseller: opt(v.diskon_reseller),
            diskonAgen: opt(v.diskon_agen),
            diskonSlot3: opt(v.diskon_slot3),
            diskonSlot4: opt(v.diskon_slot4),
            diskonSlot5: opt(v.diskon_slot5),
            diskonSlot6: opt(v.diskon_slot6),
            diskonSlot7: opt(v.diskon_slot7),
            diskonSlot8: opt(v.diskon_slot8),
            diskonSlot9: opt(v.diskon_slot9),
            diskonSlot10: opt(v.diskon_slot10),
            diskonSlot11: opt(v.diskon_slot11),
            diskonSlot12: opt(v.diskon_slot12),
            diskonSlot13: opt(v.diskon_slot13),
            diskonSlot14: opt(v.diskon_slot14),
            diskonSlot15: opt(v.diskon_slot15)
          },
          hpp: toNum(v.hpp)
        }))
    } satisfies PromoPreviewProduct;
  }
}

/** Kolom varian yang dibutuhkan tangga harga — dipakai pratinjau & pemilih. */
const PROMO_PRODUCT_VARIANT_COLS =
  "product_variants(id, name, price_normal, price_extra, hpp, sort_order, is_active, diskon_mode, diskon_reseller, diskon_agen, " +
  "diskon_slot3, diskon_slot4, diskon_slot5, diskon_slot6, diskon_slot7, diskon_slot8, diskon_slot9, diskon_slot10, diskon_slot11, diskon_slot12, diskon_slot13, diskon_slot14, diskon_slot15)";

/** Satu produk di layar PEMILIH PRODUK (seksi 3 editor kampanye). */
export type PromoPickerProduct = PromoPreviewProduct & {
  /** Status katalog (OPEN PO / READY LENGKAP / …) — admin memilih dengan sadar. */
  status: string;
  /** Foto sampul; null bila produk belum berfoto. */
  coverUrl: string | null;
};

/**
 * Daftar produk untuk LAYAR PEMILIH kampanye — bisa dicari, ditandai banyak
 * sekaligus, dan tiap barisnya membawa bahan PRATINJAU HARGA (varian + override
 * per-slot) sehingga harga promo dihitung tangga produksi, bukan rumus layar.
 *
 * Pencarian menyapu nama, merek, DAN kategori: admin yang mengetik "gamis"
 * biasanya tak peduli kata itu ada di nama atau kategorinya. Karakter khusus
 * PostgREST dibuang (idiom `searchProductOptions`) supaya ketikan biasa tak
 * pernah jadi galat query.
 *
 * Hanya produk AKTIF — kampanye tak bisa menurunkan harga produk yang tak
 * terpajang, dan menawarkannya di pemilih hanya melahirkan target mati.
 */
export async function fetchPromoPickerProducts(opts: {
  query?: string;
  productTypeScope: "preorder" | "ready_stock" | "all";
  limit?: number;
  offset?: number;
}): Promise<PromoPickerProduct[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const limit = opts.limit ?? 30;
  const offset = opts.offset ?? 0;

  let query = db
    .from("products")
    .select(
      "id, name, brand, category, product_type, status, " +
        PROMO_PRODUCT_VARIANT_COLS +
        ", product_media(media_url, is_cover, sort_order, media_type)"
    )
    .eq("is_active", true)
    .order("name", { ascending: true })
    .range(offset, offset + limit - 1);

  if (opts.productTypeScope !== "all") query = query.eq("product_type", opts.productTypeScope);

  const q = (opts.query ?? "").trim();
  if (q) {
    const safe = q.replace(/[%,()]/g, " ").trim();
    if (safe) {
      query = query.or(
        `name.ilike.%${safe}%,brand.ilike.%${safe}%,category.ilike.%${safe}%`
      );
    }
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map((row) => {
    type Media = {
      media_url: string | null;
      is_cover: boolean | null;
      sort_order: number | null;
      media_type: string | null;
    };
    const media = ((row.product_media ?? []) as Media[])
      .filter((m) => Boolean(m.media_url) && (m.media_type ?? "image") === "image")
      .sort(
        (a, b) =>
          Number(Boolean(b.is_cover)) - Number(Boolean(a.is_cover)) ||
          (a.sort_order ?? 0) - (b.sort_order ?? 0)
      );
    return {
      ...mapPromoProductRow(row),
      status: (row.status as string) ?? "",
      coverUrl: media[0]?.media_url ?? null
    } satisfies PromoPickerProduct;
  });
}

/**
 * Label ukuran yang ADA pada produk yang disasar kampanye — bahan daftar isian
 * "Potongan per Ukuran".
 *
 * Sumbernya `products.sizes` (denormalisasi mig 197, sudah dipelihara trigger)
 * BUKAN sapuan varian: kampanye bertarget merek bisa mengenai ratusan produk,
 * dan mengunduh seluruh nama variannya hanya untuk menyusun daftar ukuran akan
 * jauh lebih mahal daripada nilainya. Konsekuensinya daftar ini bisa memuat
 * label yang tak bisa dicocokkan pencocok harga (mis. "Bayi") — editor
 * menandainya lewat `isMatchableSizeLabel`, bukan menyembunyikannya.
 *
 * Filter targetnya CERMIN `fetchPromoPreviewProducts`; kalau keduanya
 * menyimpang, daftar ukuran akan berasal dari himpunan produk yang berbeda dari
 * yang benar-benar kena kampanye.
 */
export async function fetchPromoSizeOptions(opts: {
  targetType: "product" | "category" | "brand" | "all";
  targets: Array<{ kind: string; productId: string | null; name: string }>;
  productTypeScope: "preorder" | "ready_stock" | "all";
  limit?: number;
}): Promise<string[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  let query = db
    .from("products")
    .select("sizes")
    .eq("is_active", true)
    .limit(opts.limit ?? 400);

  if (opts.productTypeScope !== "all") query = query.eq("product_type", opts.productTypeScope);

  if (opts.targetType === "product") {
    const ids = opts.targets.map((t) => t.productId).filter((id): id is string => Boolean(id));
    if (!ids.length) return [];
    query = query.in("id", ids);
  } else if (opts.targetType === "brand" || opts.targetType === "category") {
    const names = opts.targets.map((t) => t.name).filter(Boolean);
    if (!names.length) return [];
    query = query.in(opts.targetType, names);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const out = new Set<string>();
  for (const row of (data as Array<{ sizes?: string[] | null }> | null) ?? []) {
    for (const size of row.sizes ?? []) {
      const label = (size ?? "").trim();
      if (label) out.add(label);
    }
  }
  return [...out];
}

export async function deletePromoCampaign(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");

  // Audit A8 (mig 5990): RPC ber-pagar — kampanye yang pernah dipakai baris
  // pesanan DITOLAK server (FK `set null` akan mengosongkan jejak laporan);
  // pesannya menyuruh menonaktifkan saja.
  const { error } = await db.rpc("admin_delete_promo_campaign", { p_id: id });
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
}
